/**
 * Promotional offers shown in the home hero.
 *
 * This module holds the STRUCTURAL half of an offer — its id, dates, link,
 * and artwork. The translated half (headline, subhead, terms, CTA label)
 * lives in i18n/messages/{locale}/offers.json, keyed by the same `id`.
 *
 * The split is deliberate. If the active list lived per-locale, the two
 * locale files could disagree about which offers exist, and a Spanish
 * visitor would see one offer where an English visitor sees two. No type
 * check catches that. One list makes it structurally impossible.
 *
 * Same shape as the blog split — see lib/constants BLOG_POST_META and
 * tests/i18n/blog-locale-completeness.test.ts.
 */

import { shopToday } from "@/lib/utils/shop-date";

/** Artwork-failure glyphs. `PromoCard`'s GLYPHS map is keyed by this union. */
export type PromoGlyph = "oil" | "tire";

export interface PromoOffer {
  /** Stable id. Joins to i18n copy and is the `?promo=` attribution value. */
  readonly id: string;
  /** Inclusive last day, `YYYY-MM-DD` in America/New_York. `null` = evergreen. */
  readonly expiresAt: string | null;
  /** Destination before the attribution parameter is appended. */
  readonly href: string;
  readonly image: string;
  /**
   * Rendered when `image` fails to load, so a card never collapses.
   * A union rather than `string` so the card's GLYPHS lookup is exhaustive.
   * While this was `string`, that lookup needed a `?? Droplet` default, and a
   * typo ("tyre") rendered an oil drop on a tire offer with nothing failing.
   */
  readonly glyph: PromoGlyph;
}

/** Hick's Law: more than two offers in a hero is choice paralysis, not choice. */
export const MAX_VISIBLE_OFFERS = 2;

/** A deadline further out than this reduces urgency rather than creating it. */
export const ENDS_SOON_DAYS = 7;

/**
 * Active promotional offers. Empty when no campaign is running, which is the
 * normal resting state rather than an error — the hero's aside slot falls back
 * to the trust card (`components/ui/hero-trust.tsx`) and keeps its shape.
 *
 * Adding one back means adding the entry here AND its copy under the same id
 * in `i18n/messages/{en-US,es-US}/offers.json`; the locale-completeness test
 * fails if either half is missing, in either direction. The two offers this
 * list previously held were never approved for publication and were removed
 * before launch — recover their shape from git history, not their terms.
 */
export const PROMO_OFFERS: readonly PromoOffer[] = [] as const;

/**
 * Today in New York as `YYYY-MM-DD`. The implementation (and the reasoning for
 * `formatToParts`) lives in lib/utils/shop-date.ts, shared with the /book form so
 * both judge "today" the same way. This name stays for existing callers and for
 * the boundary tests in tests/domain/promotions.test.ts.
 */
export const nyToday = shopToday;

/**
 * Offers to render, in authoring order.
 *
 * Filters BEFORE capping — capping first would let an expired offer occupy a
 * visible slot and silently hide a live one behind it.
 */
export function getActiveOffers(
  now: Date,
  offers: readonly PromoOffer[] = PROMO_OFFERS,
): PromoOffer[] {
  const today = nyToday(now);
  return offers
    .filter((offer) => offer.expiresAt === null || offer.expiresAt >= today)
    .slice(0, MAX_VISIBLE_OFFERS);
}

/**
 * Whether to show an "Ends <date>" cue on the card.
 *
 * Adds days on the NY calendar date, not on absolute milliseconds — a DST
 * transition inside the span would otherwise move the cutoff a full day,
 * stretching the window to 8 days in March (spring forward) and shrinking it
 * to 6 in November (fall back).
 */
export function showsEndsLabel(offer: PromoOffer, now: Date): boolean {
  if (offer.expiresAt === null) return false;
  // Parse the NY date and add ENDS_SOON_DAYS on the calendar (not absolute milliseconds).
  // Date.UTC normalizes overflow (day 35 → next month), and the result is UTC-anchored,
  // so toISOString().slice(0, 10) is exact here — no timezone re-derivation needed.
  const [year, month, day] = nyToday(now).split("-").map(Number);
  const cutoff = new Date(Date.UTC(year, month - 1, day + ENDS_SOON_DAYS));
  return offer.expiresAt <= cutoff.toISOString().slice(0, 10);
}

/**
 * Destination with the attribution parameter appended.
 *
 * The fragment is split off first and re-attached last. Appending blindly to
 * an href like `/services#tires` yields `/services#tires?promo=…`, where the
 * query sits *inside* the fragment: the attribution never reaches the server
 * and the anchor stops resolving. No offer uses a fragment today, which is
 * precisely why the next author would not notice.
 */
export function promoHref(offer: PromoOffer): string {
  const hashIndex = offer.href.indexOf("#");
  const path = hashIndex === -1 ? offer.href : offer.href.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : offer.href.slice(hashIndex);
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}promo=${encodeURIComponent(offer.id)}${fragment}`;
}
